'use client';

import { AppImageClient } from '@components/appImage';
import Link from 'next/link';
import React, { PropsWithChildren } from 'react';

import { ArticleCardProps } from './types';
import { Button } from '../../button';
import { Chip } from '../../chip';

import IconLinkButton from '@/assets/icons/iconLinkButton.svg';

export const ArticleCard = ({
  labelId,
  descriptionId,
  label,
  img,
  imageData,
  alt,
  chips,
  description,
  tags,
  urlArticle,
  variant = 'article',
}: ArticleCardProps) => {
  const Wrapper = ({ children }: PropsWithChildren) =>
    urlArticle ? (
      <Link href={urlArticle!} className="block h-full w-full">
        {children}
      </Link>
    ) : (
      <div className="block h-full w-full">{children}</div>
    );

  return (
    <Wrapper>
      <div className="rounded-20 bg-surface-base-secondary relative h-full hover:cursor-pointer max-sm:hover:cursor-default">
        <div className="rounded-20 absolute top-0 left-0 h-full w-full bg-gradient-to-l from-[rgba(255,255,255,0.192)] to-transparent opacity-0 transition-opacity duration-300 hover:opacity-100" />
        <div className="rounded-20 flex h-full w-full flex-col justify-start px-2 pt-2 pb-5">
          <div className="flex flex-1 flex-col">
            {(imageData || img) && (
              <div className="relative h-auto w-full rounded-xl">
                {chips && chips.length > 0 && (
                  <div className="absolute z-10 flex w-full justify-between p-3">
                    <div className="flex flex-wrap gap-3 max-sm:gap-2">
                      {chips.map((chip, index) => (
                        <Chip label={chip} key={index} />
                      ))}
                    </div>
                    {urlArticle && (
                      <div className="bg-custom-surface-contrast-low text-text-base-primary h-10 w-10 rounded-xl p-2 backdrop-blur-2xl max-sm:hidden">
                        <IconLinkButton />
                      </div>
                    )}
                  </div>
                )}
                {imageData ? (
                  <AppImageClient
                    {...imageData}
                    alt={alt ?? ''}
                    className="pointer-events-none h-auto w-full rounded-xl"
                  />
                ) : (
                  <img
                    src={img}
                    className="pointer-events-none w-full rounded-xl"
                    alt={alt ?? ''}
                    decoding="async"
                    loading="lazy"
                  />
                )}
              </div>
            )}
            {tags && (
              <div className="mt-4 flex flex-wrap gap-2 px-3 max-sm:gap-[6px]">
                {tags.map((tag, index) => (
                  <div key={index}>
                    <p className="text-text-base-secondary flex items-center text-[14px] leading-[18px] whitespace-nowrap max-sm:text-xs">
                      {tag}
                      {index !== tags.length - 1 && (
                        <span className="bg-text-base-secondary ml-2 h-[8px] w-[8px] rounded-[50%] max-sm:ml-[6px]" />
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <p
              id={labelId}
              className="mx-3 mt-3 line-clamp-2 text-[18px] leading-[24px] font-medium"
            >
              {label}
            </p>
            {description && (
              <p
                id={descriptionId}
                className="text-text-base-secondary mx-3 mt-3 line-clamp-4 text-[16px] leading-[24px]"
              >
                {description}
              </p>
            )}
          </div>
          {variant === 'article' && urlArticle && (
            <Button
              className="mt-5 hidden justify-center px-4 max-sm:flex"
              variant="secondary"
              ariaLabel="Читать историю"
            >
              <div className="g-1 flex">
                Читать историю
                <IconLinkButton />
              </div>
            </Button>
          )}
        </div>
      </div>
    </Wrapper>
  );
};
